import React from "react";
import { Link } from "react-router-dom";
import { Phone, Mail, MapPin, Instagram, Clock } from "lucide-react";
import { useSite } from "../context/SiteContext";

const TikTokIcon = ({ size = 16 }) => (
    <svg
        xmlns="http://www.w3.org/2000/svg"
        width={size}
        height={size}
        viewBox="0 0 24 24"
        fill="currentColor"
    >
        <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5.8 20.1a6.34 6.34 0 0 0 10.86-4.43V8.85a8.16 8.16 0 0 0 4.77 1.52V7a4.85 4.85 0 0 1-1.84-.31Z" />
    </svg>
);

export const Footer = () => {
    const { content } = useSite();
    const social = content.social || {};
    return (
        <footer
            data-testid="site-footer"
            className="bg-[#0A1F3D] text-white"
        >
            <div className="max-w-7xl mx-auto px-6 lg:px-10 py-16 grid lg:grid-cols-4 gap-10">
                <div className="lg:col-span-2 space-y-5">
                    <div className="flex items-center gap-3">
                        <div className="h-12 w-12 shrink-0 flex items-center justify-center bg-white rounded-xl">
                            <span className="font-serif-display text-[36px] leading-none text-[#0A1F3D] -mt-1">
                                K
                            </span>
                        </div>
                        <div>
                            <div className="font-serif-display text-xl">
                                {content.branding?.name}
                            </div>
                            <div className="text-[10px] tracking-[0.18em] uppercase text-white/70">
                                {content.branding?.tagline}
                            </div>
                        </div>
                    </div>
                    <p className="text-white/70 max-w-md leading-relaxed text-sm">
                        Premium tədris akademiyası. Əvvəl psixoloq, sonra tədris —
                        hər şagird üçün fərdi yanaşma.
                    </p>
                    <div className="flex items-center gap-3 pt-2">
                        {social.instagram && (
                            <a
                                href={social.instagram}
                                target="_blank"
                                rel="noreferrer"
                                data-testid="footer-instagram"
                                className="h-9 w-9 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors"
                                aria-label="Instagram"
                            >
                                <Instagram size={16} />
                            </a>
                        )}
                        {social.tiktok && (
                            <a
                                href={social.tiktok}
                                target="_blank"
                                rel="noreferrer"
                                data-testid="footer-tiktok"
                                className="h-9 w-9 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors"
                                aria-label="TikTok"
                            >
                                <TikTokIcon size={16} />
                            </a>
                        )}
                        {social.whatsapp && (
                            <a
                                href={`https://wa.me/${social.whatsapp.replace(/\D/g, "")}`}
                                target="_blank"
                                rel="noreferrer"
                                data-testid="footer-whatsapp"
                                className="h-9 px-3 rounded-full bg-[#25D366] text-white flex items-center gap-2 text-sm hover:bg-[#1ebe57] transition-colors"
                                aria-label="WhatsApp"
                            >
                                WhatsApp
                            </a>
                        )}
                    </div>
                </div>

                <div>
                    <div className="text-xs tracking-[0.2em] uppercase text-white/50 mb-5">
                        Naviqasiya
                    </div>
                    <ul className="space-y-3 text-sm text-white/80">
                        <li><Link to="/" className="ka-link">Ana səhifə</Link></li>
                        <li><Link to="/kurslar" className="ka-link">Kurslarımız</Link></li>
                        <li><Link to="/muellimler" className="ka-link">Müəllimlər</Link></li>
                        <li><Link to="/psixoloq" className="ka-link">Psixoloq</Link></li>
                        <li><Link to="/yay-proqrami" className="ka-link">Yay proqramı</Link></li>
                        <li><Link to="/qalereya" className="ka-link">Qalereya</Link></li>
                        <li><Link to="/reyler" className="ka-link">Valideyn rəyləri</Link></li>
                        <li><Link to="/elaqe" className="ka-link">Əlaqə</Link></li>
                    </ul>
                </div>

                <div>
                    <div className="text-xs tracking-[0.2em] uppercase text-white/50 mb-5">
                        Əlaqə
                    </div>
                    <ul className="space-y-4 text-sm text-white/80">
                        <li className="flex items-start gap-3">
                            <Phone size={16} className="mt-0.5 text-white/60" />
                            <a
                                href={`tel:${content.contact?.phone}`}
                                data-testid="footer-phone"
                                className="tabular"
                            >
                                {content.contact?.phone}
                            </a>
                        </li>
                        <li className="flex items-start gap-3">
                            <Mail size={16} className="mt-0.5 text-white/60" />
                            <a href={`mailto:${content.contact?.email}`}>
                                {content.contact?.email}
                            </a>
                        </li>
                        <li className="flex items-start gap-3">
                            <MapPin size={16} className="mt-0.5 text-white/60" />
                            <span>{content.contact?.address}</span>
                        </li>
                        <li className="flex items-start gap-3">
                            <Clock size={16} className="mt-0.5 text-white/60" />
                            <span>{content.contact?.workingHours}</span>
                        </li>
                    </ul>
                </div>
            </div>

            <div className="border-t border-white/10">
                <div className="max-w-7xl mx-auto px-6 lg:px-10 py-5 flex items-center justify-center text-xs text-white/50">
                    <div>
                        © {new Date().getFullYear()} KA Academy. Bütün hüquqlar
                        qorunur.
                    </div>
                </div>
            </div>
        </footer>
    );
};

export default Footer;
